export const VALID_US_STATE_CODES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI',
  'WY',
] as const

export const VALID_US_TERRITORY_CODES = ['AS', 'GU', 'MP', 'PR', 'VI'] as const

export const VALID_CA_PROVINCE_CODES = [
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE',
  'QC', 'SK', 'YT',
] as const

export type USStateCode = (typeof VALID_US_STATE_CODES)[number]
export type USTerritoryCode = (typeof VALID_US_TERRITORY_CODES)[number]
export type CAProvinceCode = (typeof VALID_CA_PROVINCE_CODES)[number]
export type ValidStateCode = USStateCode | USTerritoryCode | CAProvinceCode
export type CountryCode = 'US' | 'CA'

const US_CODES_SET = new Set<string>([
  ...VALID_US_STATE_CODES,
  ...VALID_US_TERRITORY_CODES,
])
const CA_CODES_SET = new Set<string>(VALID_CA_PROVINCE_CODES)
const ALL_CODES_SET = new Set<string>([
  ...VALID_US_STATE_CODES,
  ...VALID_US_TERRITORY_CODES,
  ...VALID_CA_PROVINCE_CODES,
])

export const US_STATE_CODE_TO_NAME: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas',
  CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware',
  DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii',
  ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine',
  MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska',
  NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
  NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
  SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas',
  UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  AS: 'American Samoa', GU: 'Guam', MP: 'Northern Mariana Islands',
  PR: 'Puerto Rico', VI: 'U.S. Virgin Islands',
}

export const CA_PROVINCE_CODE_TO_NAME: Record<string, string> = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba',
  NB: 'New Brunswick', NL: 'Newfoundland and Labrador', NS: 'Nova Scotia',
  NT: 'Northwest Territories', NU: 'Nunavut', ON: 'Ontario',
  PE: 'Prince Edward Island', QC: 'Quebec', SK: 'Saskatchewan',
  YT: 'Yukon',
}

export interface Region {
  readonly code: string
  readonly name: string
  readonly country: CountryCode
}

export const US_STATES: readonly Region[] = VALID_US_STATE_CODES.map((code) => ({
  code,
  name: US_STATE_CODE_TO_NAME[code],
  country: 'US' as const,
}))

export const US_TERRITORIES: readonly Region[] = VALID_US_TERRITORY_CODES.map(
  (code) => ({
    code,
    name: US_STATE_CODE_TO_NAME[code],
    country: 'US' as const,
  })
)

export const CANADIAN_PROVINCES: readonly Region[] = VALID_CA_PROVINCE_CODES.map((code) => ({
  code,
  name: CA_PROVINCE_CODE_TO_NAME[code],
  country: 'CA' as const,
}))

export const ALL_REGIONS: readonly Region[] = [
  ...US_STATES,
  ...US_TERRITORIES,
  ...CANADIAN_PROVINCES,
]

const ALL_REGION_MAP: Record<string, string> = {
  ...US_STATE_CODE_TO_NAME,
  ...CA_PROVINCE_CODE_TO_NAME,
}

export const STATE_FORMAT = {
  canonical: 'CA',
  pattern: /^[A-Z]{2}$/,

  validate(value: string, country?: CountryCode): boolean {
    return this.normalize(value, country) !== undefined
  },

  normalize(value: string | undefined | null, country?: CountryCode): string | undefined {
    if (!value || value.trim() === '') return undefined
    const normalized = value.trim().toUpperCase()
    if (!this.pattern.test(normalized)) return undefined

    if (country === 'CA') {
      return CA_CODES_SET.has(normalized) ? normalized : undefined
    }
    if (country === 'US') {
      return US_CODES_SET.has(normalized) ? normalized : undefined
    }
    return ALL_CODES_SET.has(normalized) ? normalized : undefined
  },

  isUSState(value: string): boolean {
    return US_CODES_SET.has(value.toUpperCase().trim())
  },

  isCAProvince(value: string): boolean {
    return CA_CODES_SET.has(value.toUpperCase().trim())
  },

  errorMessage: 'Enter a valid 2-letter state code',
} as const

export function getStateNameFromCode(
  code: string,
  country: string = 'US'
): string | null {
  const normalizedCode = code.toUpperCase().trim()
  const normalizedCountry = country.toUpperCase().trim()

  if (normalizedCountry === 'US' || normalizedCountry === 'USA') {
    return US_STATE_CODE_TO_NAME[normalizedCode] ?? null
  }

  if (normalizedCountry === 'CA' || normalizedCountry === 'CAN') {
    return CA_PROVINCE_CODE_TO_NAME[normalizedCode] ?? null
  }

  return ALL_REGION_MAP[normalizedCode] ?? null
}

export function getStateSlugFromCode(
  code: string,
  country: string = 'US'
): string | null {
  const name = getStateNameFromCode(code, country)
  if (!name) return null
  return name.toLowerCase().replace(/\s+/g, '-')
}

export function getStateName(code: string): string | undefined {
  return US_STATE_CODE_TO_NAME[code]
}

export function getProvinceName(code: string): string | undefined {
  return CA_PROVINCE_CODE_TO_NAME[code]
}

export function getRegionName(code: string): string | undefined {
  return ALL_REGION_MAP[code]
}

export function getCountryFromRegion(code: string): CountryCode | undefined {
  if (US_CODES_SET.has(code)) return 'US'
  if (CA_CODES_SET.has(code)) return 'CA'
  return undefined
}

export function getStateCode(name: string): string | undefined {
  const lower = name.toLowerCase()
  return US_STATES.find(
    (usState) => usState.name.toLowerCase() === lower || usState.code.toLowerCase() === lower
  )?.code
}

export function getRegionCode(name: string): string | undefined {
  const lower = name.toLowerCase()
  return ALL_REGIONS.find(
    (region) => region.name.toLowerCase() === lower || region.code.toLowerCase() === lower
  )?.code
}

export function stateToSlug(abbr: string | undefined): string {
  const name = getStateName(abbr?.toUpperCase() ?? '')
  return name?.toLowerCase().replace(/\s+/g, '-') || ''
}

export function slugToStateCode(slug: string | undefined): string | undefined {
  if (!slug) return undefined
  const normalized = slug.toLowerCase().replace(/-/g, ' ')
  return US_STATES.find((usState) => usState.name.toLowerCase() === normalized)?.code
}

export function slugToStateName(slug: string | undefined): string | undefined {
  if (!slug) return undefined
  const normalized = slug.toLowerCase().replace(/-/g, ' ')
  return US_STATES.find((usState) => usState.name.toLowerCase() === normalized)?.name
}

export function normalizeStateToSlug(stateInput: string | undefined): string {
  if (!stateInput) return ''
  const upper = stateInput.toUpperCase().trim()
  if (US_STATE_CODE_TO_NAME[upper]) {
    return stateToSlug(upper)
  }
  const normalized = stateInput.toLowerCase().replace(/-/g, ' ')
  const state = US_STATES.find((usState) => usState.name.toLowerCase() === normalized)
  if (state) {
    return stateToSlug(state.code)
  }
  return stateInput.toLowerCase().replace(/\s+/g, '-')
}

export function isStateCode(value: string, country: string = 'US'): boolean {
  return getStateNameFromCode(value, country) !== null
}

const NAME_TO_STATE_CODE: Record<string, string> = Object.fromEntries(
  Object.entries({ ...US_STATE_CODE_TO_NAME, ...CA_PROVINCE_CODE_TO_NAME }).map(
    ([code, name]) => [name.toLowerCase(), code],
  ),
)

export function toStateCode(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  if (!trimmed) return undefined
  const upper = trimmed.toUpperCase()
  if (US_STATE_CODE_TO_NAME[upper] || CA_PROVINCE_CODE_TO_NAME[upper]) {
    return upper
  }
  return NAME_TO_STATE_CODE[trimmed.toLowerCase()] ?? trimmed
}
